import { Link, type LinkProps } from '@tanstack/react-router';
import type { ForwardRefExoticComponent, SVGProps } from 'react';
import { cn } from '@/shared/lib/utils';
import { Separator } from '@/shared/ui/separator/Separator';
import { Text } from '@/shared/ui/text';
import { type FlyoutItem, HoverFlyoutMenu } from './HoverFlyoutMenu';

export type NavMenuItem = {
  key: string;
  icon?: ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;
  label: string;
  to: NonNullable<LinkProps['to']>;
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  isActive: boolean;
  isDisabled?: boolean;
  hasBottomSeparator?: boolean;
  sectionHeader?: string;
  /** 子メニュー項目（展開時はインデント表示、折りたたみ時はホバーフライアウトに親リンクと並べて表示） */
  children?: NavMenuChildItem[];
};

export type NavMenuChildItem = {
  key: string;
  label: string;
  to: NonNullable<LinkProps['to']>;
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  isActive: boolean;
};

type Props = {
  items: NavMenuItem[];
  /** サイドバーが開いているか（折りたたみ時はアイコンのみ表示） */
  isOpen?: boolean;
};

const renderItemContent = ({
  item,
  isOpen,
}: {
  item: NavMenuItem | NavMenuChildItem;
  isOpen: boolean;
  /** 子項目用フラグ。アイコンなし & インデント表示 */
}) => {
  // NavMenuItem のみ持つプロパティを安全に扱う
  const Icon = 'icon' in item ? item.icon : undefined;
  const isDisabled = 'isDisabled' in item ? item.isDisabled : false;

  const iconAndLabel = (
    <>
      {Icon && <Icon className="size-4 shrink-0 text-text-muted" />}
      {isOpen && (
        <Text
          as="span"
          truncate
          className={cn(Icon && 'ml-2', item.isActive ? 'text-text-base' : 'text-text-secondary')}
        >
          {item.label}
        </Text>
      )}
    </>
  );

  const baseClassName = cn(
    'flex items-center',
    'h-9',
    'rounded-md',
    isOpen ? 'w-full px-3' : 'w-9 justify-center',
    'overflow-hidden',
  );

  if (isDisabled) {
    return <div className={cn(baseClassName, 'cursor-not-allowed opacity-40')}>{iconAndLabel}</div>;
  }

  return (
    <Link
      to={item.to}
      params={item.params}
      search={item.search}
      className={cn(
        baseClassName,
        'transition-all duration-200',
        item.isActive ? 'bg-surface-strength2' : 'hover:bg-[rgba(12,10,9,0.04)]',
        'cursor-pointer',
      )}
    >
      {iconAndLabel}
    </Link>
  );
};

const renderChildItem = ({ item }: { item: NavMenuChildItem }) => (
  <Link
    key={item.key}
    to={item.to}
    params={item.params}
    search={item.search}
    className={cn(
      'flex h-9 items-center rounded-md pr-3 pl-9 transition-all duration-200',
      item.isActive ? 'bg-surface-strength2' : 'hover:bg-[rgba(12,10,9,0.04)]',
      'cursor-pointer',
    )}
  >
    <Text
      as="span"
      truncate
      className={cn(item.isActive ? 'text-text-base' : 'text-text-secondary')}
    >
      {item.label}
    </Text>
  </Link>
);

const toFlyoutItems = (item: NavMenuItem): FlyoutItem[] => {
  const parent: FlyoutItem = {
    key: item.key,
    label: item.label,
    to: item.to,
    params: item.params,
    search: item.search,
    isActive: item.isActive,
    isDisabled: item.isDisabled,
  };
  if (!item.children || item.children.length === 0) {
    return [parent];
  }
  return [parent, ...item.children];
};

/**
 * ナビゲーションメニューアイテムのレンダリング
 *
 * 第1階層（SidePanel）と第2階層（SecondaryPanel）で共通利用する。
 * - 展開時: 親項目の下に children をインデント表示する
 * - 折りたたみ時: 各アイコンを HoverFlyoutMenu でラップし、親リンク + 子リンクを表示する
 */
export const NavMenuItems = ({ items, isOpen = true }: Props) => {
  return (
    <nav className="flex flex-col">
      {items.map((item) => {
        const hasChildren = !!item.children && item.children.length > 0;
        // 折りたたみ時は子項目アイコンが見えないため、子がアクティブな場合は
        // 親アイコンに active 背景を当てて現在地が分かるようにする
        const hasActiveChild =
          !isOpen && hasChildren && (item.children?.some((child) => child.isActive) ?? false);
        const renderedItem = hasActiveChild ? { ...item, isActive: true } : item;
        const content = renderItemContent({ item: renderedItem, isOpen });

        return (
          <div key={item.key}>
            {item.sectionHeader && isOpen && (
              <Text className="mt-2 mb-2 whitespace-nowrap px-3" variant={'description'}>
                {item.sectionHeader}
              </Text>
            )}
            {isOpen ? (
              <>
                {content}
                {hasChildren && (
                  <div className="flex flex-col">
                    {item.children?.map((child) => renderChildItem({ item: child }))}
                  </div>
                )}
              </>
            ) : (
              <HoverFlyoutMenu items={toFlyoutItems(item)}>{content}</HoverFlyoutMenu>
            )}
            {item.hasBottomSeparator && <Separator className="my-1" />}
          </div>
        );
      })}
    </nav>
  );
};
